import { useEffect, useRef, useState } from "react";
import { addDoc, collection, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import AssistanceTasks from "./AssistanceTasks";

const emptyClient = { firstName: "", lastName: "", personalNumber: "", address: "", phone: "" };
const fields = [
  { name: "firstName", label: "Förnamn", autoComplete: "given-name" },
  { name: "lastName", label: "Efternamn", autoComplete: "family-name" },
  { name: "personalNumber", label: "Personnummer", placeholder: "ÅÅÅÅMMDD-XXXX", pattern: "(?:[0-9]{6}|[0-9]{8})[-+]?[0-9]{4}", title: "Ange 10 eller 12 siffror, med eller utan bindestreck." },
  { name: "address", label: "Adress", autoComplete: "street-address" },
  { name: "phone", label: "Telefonnummer", type: "tel", autoComplete: "tel" },
];

export default function AssistanceClients({ companyUid }) {
  const [form, setForm] = useState(emptyClient);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(Boolean(companyUid));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [message, setMessage] = useState("");
  const [tasksClient, setTasksClient] = useState(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!companyUid) return;
    return onSnapshot(collection(db, "users", companyUid, "clients"), (snapshot) => {
      setClients(snapshot.docs.map((client) => ({ ...client.data(), id: client.id }))
        .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, "sv")));
      setLoading(false);
      setLoadError("");
    }, () => {
      setLoading(false);
      setLoadError("Kunde inte hämta klienterna. Försök ladda om sidan.");
    });
  }, [companyUid]);

  const saveClient = async (event) => {
    event.preventDefault();
    if (!companyUid || savingRef.current) return;
    const client = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()]));
    setError("");
    setMessage("");
    if (Object.values(client).some((value) => !value)) {
      setError("Fyll i alla klientuppgifter.");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      await addDoc(collection(db, "users", companyUid, "clients"), { ...client, createdAt: serverTimestamp() });
      setForm(emptyClient);
      setMessage("Klienten har sparats.");
    } catch {
      setError("Kunde inte spara klienten. Försök igen.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return <>
    {tasksClient && <div className="smart-assistance__tasks-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setTasksClient(null); }} onKeyDown={(event) => { if (event.key === "Escape") setTasksClient(null); }}><section className="smart-assistance__tasks-dialog" role="dialog" aria-modal="true" aria-label={`Uppgifter för ${tasksClient.firstName} ${tasksClient.lastName}`}><div className="smart-assistance__tasks-dialog-header"><h2>Uppgifter för {tasksClient.firstName} {tasksClient.lastName}</h2><button type="button" aria-label="Stäng" onClick={() => setTasksClient(null)}>Stäng</button></div><AssistanceTasks key={tasksClient.id} companyUid={companyUid} clientId={tasksClient.id} clientName={`${tasksClient.firstName} ${tasksClient.lastName}`} /></section></div>}
    {!companyUid && <p role="status">Välj ett företag för att skapa och visa klienter.</p>}
    <form className="smart-assistance__client-form" onSubmit={saveClient}>
      <fieldset disabled={!companyUid || saving}>
        <legend>Skapa klient</legend>
        <div className="smart-assistance__client-fields">
          {fields.map(({ name, label, ...attributes }) => <label key={name}>
            {label}
            <input {...attributes} name={name} required maxLength={name === "address" ? 500 : 100}
              value={form[name]} onChange={(event) => { setForm({ ...form, [name]: event.target.value }); setMessage(""); }} />
          </label>)}
        </div>
        <button type="submit">{saving ? "Sparar…" : "Spara klient"}</button>
      </fieldset>
    </form>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    <h3>Sparade klienter</h3>
    {loadError && <p role="alert">{loadError}</p>}
    {loading ? <p role="status">Hämtar klienter…</p> : companyUid && !loadError && clients.length === 0 ? <p>Inga klienter har lagts till ännu.</p> : null}
    {clients.length > 0 && <div className="smart-assistance__client-list">
      <table>
        <caption className="smart-assistance__visually-hidden">Sparade klientuppgifter</caption>
        <thead><tr>{fields.map(({ name, label }) => <th scope="col" key={name}>{label}</th>)}<th scope="col">Uppgifter</th></tr></thead>
        <tbody>{clients.map((client) => <tr key={client.id}>{fields.map(({ name }) => <td key={name}>{client[name]}</td>)}<td><button type="button" className="smart-assistance__client-tasks-button" onClick={() => setTasksClient(client)}>Skapa uppgifter</button></td></tr>)}</tbody>
      </table>
    </div>}
  </>;
}
